import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLogger } from '@direct-frontend/modules-logger';
import { useHover } from 'react-aria';
import { genericMemo } from '@direct-frontend/stdlib';

import { Tooltip } from '../../Tooltip';
import { cn } from '../../../lib/css/className/className';
import { useSidebarCollapsedState } from '../SidebarCollapsedStateProvider';
import { getColorSkin } from '../../../lib/skin/getColorSkin/getColorSkin';

import { i18n } from './SidebarResizer.i18n';

import './SidebarResizer.css';

const cnSidebarResizer = cn('SidebarResizer');

interface Config {
    isCollapsed: boolean;
    isFinished: boolean;
    startX: number;
}

export const SidebarResizer = genericMemo(function SidebarResizer() {
    const { LogProvider, logClick } = useLogger('SidebarResizer');
    const { isCollapsed, onToggleIsCollapsed } = useSidebarCollapsedState();
    const { hoverProps, isHovered } = useHover({});

    const onToggleHandler = useCallback(() => {
        logClick();
        onToggleIsCollapsed();
    }, [logClick, onToggleIsCollapsed]);

    const [isHover, setIsHover] = useState<boolean>(false);
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const ref = useRef<HTMLDivElement>(null);
    const configRef = useRef<Config>({ isCollapsed, isFinished: false, startX: 0 });
    const colorSkinClass = getColorSkin(isHovered ? 'gray-low-contrast' : 'gray-transparent', {
        isHovered: isHover || isHovered || isDragging,
    });

    configRef.current.isCollapsed = isCollapsed;

    const onMouseEnter = useCallback((event: React.MouseEvent) => {
        event.preventDefault();
        setIsHover(true);
    }, []);

    const onMouseLeave = useCallback((event: React.MouseEvent) => {
        event.preventDefault();
        setIsHover(false);
    }, []);

    useEffect(() => {
        const resizer = ref.current;

        if (!resizer) {
            return;
        }

        function onMouseMove(event: MouseEvent) {
            const { isCollapsed, isFinished, startX } = configRef.current;

            if (
                !isFinished &&
                ((isCollapsed && startX - event.clientX < 0) || (!isCollapsed && startX - event.clientX > 0))
            ) {
                configRef.current.isFinished = true;
                document.removeEventListener('mousemove', onMouseMove);
                onToggleHandler();
            }
        }

        function onMouseDown(event: MouseEvent) {
            configRef.current.isFinished = false;
            configRef.current.startX = event.clientX;

            document.addEventListener('mousemove', onMouseMove);
            setIsDragging(true);
        }

        function onMouseUp() {
            configRef.current.isFinished = false;
            document.removeEventListener('mousemove', onMouseMove);
            setIsDragging(false);
        }

        resizer.addEventListener('click', onToggleHandler);
        resizer.addEventListener('mousedown', onMouseDown);
        document.addEventListener('mouseup', onMouseUp);

        return () => {
            resizer.removeEventListener('click', onToggleHandler);
            resizer.removeEventListener('mousedown', onMouseDown);
            document.removeEventListener('mouseup', onMouseUp);
            document.removeEventListener('mousemove', onMouseMove);
        };
    }, [onToggleHandler]);

    const title = isCollapsed ? i18n('Потяните, чтобы развернуть') : i18n('Потяните, чтобы свернуть');

    return (
        <LogProvider>
            <div
                {...hoverProps}
                className={cnSidebarResizer(
                    '',
                    {
                        collapsed: isCollapsed,
                        finished: configRef.current.isFinished,
                    },
                    [colorSkinClass],
                )}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                ref={ref}
                data-testid="SidebarResizer"
            />
            <Tooltip
                triggerRef={ref}
                isModal={false}
                isDismissable={false}
                placement="right"
                isOpen={isHover && !isDragging}
                color="inverted"
                data-testid="SidebarResizer.Popup"
                title={title}
                logNode={isCollapsed ? 'Expand' : 'Collapse'}
            />
        </LogProvider>
    );
});
